import { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import Icon from 'pages/Properties/Feed/Icons';
import { useLocation } from 'react-router-dom';
import { LocationService } from 'services';
import { getFeedSortSelector } from 'store/selectors/feed';
import { sortFeedEffect } from 'store/effects';
import { useOutsideClick } from 'hooks';
import { OptionsPopup } from 'components';
import Fields from './Fields';
import styles from './styles.module.scss';
import { getUserRolesMapSelector } from 'store/selectors/user';
import { SORT_ASC, SORT_DESC } from 'settings/constants/sort';

const fieldsPriority = [
  'MatchScore',
  'ClientsMatched',
  'SellingPrice',
  'DaysOnMarket',
  'NumBeds',
  'NumBathsTotal',
  'SquareFeet',
].reduce((acc, field, index) => ({ ...acc, [field]: index }), {});

const locationService = new LocationService();

const Sort = ({
  className,
  sortCases,
  customleft,
  customright,
  offsetLeft,
  isFavortiesSectionVisible,
}) => {
  const dispatch = useDispatch();
  const sort = useSelector(getFeedSortSelector);
  const location = useLocation();
  const { isClient } = useSelector(getUserRolesMapSelector);

  const query = locationService.setLocation(location).getQuery();

  const [open, setOpen] = useState(false);

  const sortButtonRef = useRef();
  const popupRef = useRef();

  useOutsideClick([popupRef, sortButtonRef], () => setOpen(false));

  const onClickHandler = () => {
    setOpen(!open);
  };

  const onChangeFields = (fieldNames, order) => {
    const sortedFields = fieldNames.sort((o1, o2) => fieldsPriority[o1] - fieldsPriority[o2]);
    dispatch(
      sortFeedEffect({
        order,
        fields: sortedFields,
      }),
    );
    setOpen(false);
  };

  const activeFields = useMemo(() => {
    if (sort?.fields?.length) return sort?.fields;

    return [isClient ? (isFavortiesSectionVisible ? '' : 'MatchScore') : 'DaysOnMarket'];
  }, [sort?.fields, isFavortiesSectionVisible]);

  return (
    <div>
      <div
        ref={sortButtonRef}
        onClick={onClickHandler}
        className={classNames(styles.sort, className)}
      >
        Sort by:{' '}
        <span>
          {
            [...Sort.defaultProps.sortCases, ...Sort.agentSortCases]?.find(
              ({ value, order }) => value === activeFields?.[0] && order === sort.order,
            )?.label
          }{' '}
        </span>
        <Icon testid="sort_icon" className={styles.icon} variant={Icon.SORT} />
      </div>
      <OptionsPopup
        className={styles.popup}
        ref={popupRef}
        offsetTop={5}
        parentRef={sortButtonRef}
        open={open}
        customleft={customleft}
        customright={customright}
        offsetLeft={offsetLeft}
      >
        <Fields
          order={sort.order}
          onChange={onChangeFields}
          list={sortCases}
          activeFields={activeFields}
        />
      </OptionsPopup>
    </div>
  );
};

Sort.propTypes = {
  className: PropTypes.string,
  sortCases: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  alphabeticalSort: PropTypes.bool,
  customleft: PropTypes.number,
  customright: PropTypes.number,
  offsetLeft: PropTypes.number,
  isFavortiesSectionVisible: PropTypes.bool,
};

Sort.defaultProps = {
  isFavortiesSectionVisible: false,
  offsetLeft: 0,
  className: '',
  sortCases: [
    { label: 'Match Score', value: 'MatchScore', order: SORT_DESC },
    { label: 'Price (highest first)', value: 'SellingPrice', order: SORT_DESC },
    { label: 'Price (lowest first)', value: 'SellingPrice', order: SORT_ASC },
    { label: 'Days on Market', value: 'DaysOnMarket', order: SORT_ASC },
    { label: 'Beds', value: 'NumBeds', order: SORT_DESC },
    { label: 'Baths', value: 'NumBathsTotal', order: SORT_DESC },
    { label: 'Square Feet', value: 'SquareFeet', order: SORT_DESC },
  ],
};

Sort.agentSortCases = [{ label: 'Client Match', value: 'ClientsMatched', order: SORT_DESC }];

export default Sort;
